import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ButtonBase } from "@mui/material";
import { IconEdit, IconTrash, IconPlus } from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  "&:hover": {
    background: theme.palette.error.dark,
    color: theme.palette.error.light,
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: "pointer",
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.info.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  align: "center",
  gap: 8,
  width: 150,
  height: 40,
  borderRadius: 10,
  display: "flex",
  cursor: "pointer",
}));

const CitiesList = () => {
  const [cities, setCities] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCity, setCurrentCity] = useState({ name: "", state: "" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const fetchCities = useCallback(() => {
    const searchText = searchTerm !== "" ? `&contains=${searchTerm}` : "";
    fetch(
      `/backend/cities/?page=${page + 1}&page_size=${rowsPerPage}${searchText}`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Falha ao carregar as cidades");
        }
        return response.json();
      })
      .then((data) => {
        setCities(data.results);
        setTotalRecords(data.count);
      });
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities, page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (city) => {
    setCurrentCity(city);
    setIsEditing(true);
    setOpenModal(true);
  };

  const handleAdd = () => {
    setCurrentCity({ name: "", state: "" });
    setIsEditing(false);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleSave = () => {
    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/cities/${currentCity.id}/`
      : "/backend/cities/";

    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(currentCity),
    })
      .then((response) => {
        if (response.ok) return response.json();
        throw new Error("Erro ao salvar cidade");
      })
      .then((data) => {
        if (isEditing) {
          setCities((prevCities) =>
            prevCities.map((city) => (city.id === data.id ? data : city))
          );
        } else {
          setCities((prevCities) => [...prevCities, data]);
        }
        alert("Cidade salva com sucesso!");
        setOpenModal(false);
        fetchCities();
      })
      .catch((error) => alert("Erro: " + error.message));
  };

  const handleDelete = (cityId) => {
    if (window.confirm("Você tem certeza que deseja excluir esta cidade?")) {
      fetch(`/backend/cities/${cityId}/`, {
        method: "DELETE",
      })
        .then((response) => {
          if (response.ok) {
            setCities((prevCities) =>
              prevCities.filter((city) => city.id !== cityId)
            );
            alert("Cidade excluída com sucesso!");
            fetchCities();
          } else {
            throw new Error("Erro ao excluir cidade");
          }
        })
        .catch((error) => alert("Erro: " + error.message));
    }
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de cidades"
        actions={[
          <AddButton onClick={handleAdd}>
            <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Adicionar cidade
          </AddButton>,
        ]}
      >
        <SearchSection onSearchChange={setSearchTerm} />
        <Divider sx={{ my: 2 }} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>UF</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cities.map((city) => (
              <TableRow key={city.id}>
                <TableCell>{city.name}</TableCell>
                <TableCell>{city.state}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <EditButton onClick={() => handleEdit(city)}>
                      <IconEdit stroke={1.5} size="1.3rem" />
                    </EditButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <DeleteButton onClick={() => handleDelete(city.id)}>
                      <IconTrash stroke={1.5} size="1.3rem" />
                    </DeleteButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>
          {isEditing ? "Editar Cidade" : "Adicionar Cidade"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Nome"
            value={currentCity.name}
            onChange={(e) =>
              setCurrentCity({ ...currentCity, name: e.target.value })
            }
            fullWidth
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            label="UF"
            value={currentCity.state}
            onChange={(e) =>
              setCurrentCity({ ...currentCity, state: e.target.value })
            }
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CitiesList;
